import React from 'react';
import { KpiTile } from '~analytics-components/kpi-tile';
import type { KpiTileData } from '~analytics-components/kpi-tile';
import style from './boxed-tile-row.module.scss';

export interface BoxedTileRowProps {
    tiles: KpiTileData[];
    /** The one tile that gets the grey box; every other tile gets the white
     * bordered box. */
    highlightIndex: number;
    /** Tiles side by side before the row wraps: three when omitted. */
    columns?: number;
}

/** "N/A", "Date N/A": a tile whose value is a status, not a figure. */
const hasNoFigure = (tile: KpiTileData) =>
    typeof tile.value === 'string' && !/\d/.test(tile.value);

/** A row of tiles, each boxed on its own (rounded, bordered), with one tile
 * picked out on a grey fill instead of the white/bordered default. Formatting
 * (currency, decimals, Date N/A) stays on `KpiTile`, rendered flat inside the
 * box; in rows of several tiles, long figures shorten (`$38.2k`) with the
 * exact one on hover. */
export const BoxedTileRow: React.FC<BoxedTileRowProps> = ({
    tiles,
    highlightIndex,
    columns,
}) => (
    <div
        className={style.boxed_tile_row}
        style={
            columns
                ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
                : undefined
        }
    >
        {tiles.map((tile, index) => (
            <div
                key={tile.title ?? index}
                className={`${style.boxed_tile} ${
                    index === highlightIndex ? style.highlighted : ''
                } ${hasNoFigure(tile) ? style.no_figure : ''}`}
            >
                <KpiTile
                    // A lone tile has the whole card for its figure.
                    kpi={{ ...tile, flat: true, compact: tiles.length > 1 }}
                />
            </div>
        ))}
    </div>
);
